import React, { forwardRef, useImperativeHandle } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { useUserState } from '@/components/context/UserContext';
import { useSettingsState } from '@/components/context/SettingsContext';
import { Trans, useTranslation } from 'react-i18next';
import {
  isBlacklistedEmail,
  blacklistedEmailMessage,
} from '@/components/shared/Validator/emailBlacklist';

const REQUIRED = 'Privalomas laukas';
const trim = (v) => (typeof v === 'string' ? v.trim() : v);
const zTrimmedRequired = z.preprocess(trim, z.string().min(1, REQUIRED));
const zTrimmedEmail = z
  .preprocess(
    trim,
    z.string().min(1, REQUIRED).email('Neteisingas el. pašto adresas'),
  )
  .refine((v) => /^[\u0000-\u007F]+$/.test(v), {
    message: 'Leidžiami tik lotyniški simboliai',
  })
  .refine((val) => !isBlacklistedEmail(val), {
    message: blacklistedEmailMessage,
  });

const BuyerInfoForm = forwardRef((props, ref) => {
  const userState = useUserState();
  const { agreements } = useSettingsState();
  const { t } = useTranslation();

  const schema = z.object({
    patientFirstName: zTrimmedRequired,
    patientLastName: zTrimmedRequired,
    payerPhone: zTrimmedRequired,
    payerEmail: zTrimmedEmail,

    agreePrivacy: z.boolean().refine((v) => v === true, REQUIRED),
    agreeCouponRules: z.boolean().refine((v) => v === true, REQUIRED),
  });

  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      patientFirstName: userState?.firstName || '',
      patientLastName: userState?.lastName || '',
      payerPhone: userState?.phoneNumber || '',
      payerEmail: userState?.email || '',

      agreePrivacy: false,
      agreeCouponRules: false,
    },
  });

  useImperativeHandle(ref, () => ({
    trigger: async () => {
      const valid = await form.trigger();
      if (!valid) {
        scrollToFirstError(form.formState.errors);
      }
      return valid;
    },
    getValues: form.getValues,
  }));

  const scrollToFirstError = (errors) => {
    const firstErrorKey = Object.keys(errors)[0];
    if (firstErrorKey) {
      const el = document.querySelector(`[name="${firstErrorKey}"]`);
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el?.focus?.();
    }
  };

  return (
    <Form {...form}>
      <form className="space-y-6">
        <FormField
          name="patientFirstName"
          control={form.control}
          disabled={userState?.firstName != undefined}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Vardas</FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          name="patientLastName"
          control={form.control}
          disabled={userState?.lastName != undefined}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Pavardė</FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          name="payerPhone"
          control={form.control}
          disabled={userState?.phoneNumber != undefined}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Telefono numeris</FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          name="payerEmail"
          disabled={userState?.email != undefined}
          control={form.control}
          render={({ field }) => (
            <FormItem>
              <FormLabel>El. pašto adresas</FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <section className="w-full space-y-3">
          <FormLabel>{t('cart.agreementGroulLabel')}</FormLabel>
          <FormField
            control={form.control}
            name="agreePrivacy"
            render={({ field }) => (
              <FormItem
                className="flex items-start gap-2 [&_[data-slot=label]]:leading-5"
                data-error-anchor={field.name}
              >
                <FormControl>
                  <Checkbox
                    id="agree-privacy"
                    className="mt-[2px]"
                    checked={!!field.value}
                    onCheckedChange={(v) => field.onChange(Boolean(v))}
                  />
                </FormControl>
                <div className="space-y-1">
                  <Label htmlFor="agree-privacy" layout="text">
                    <Trans
                      i18nKey="cart.fieldAgreement2Label"
                      components={{
                        a: (
                          <a
                            href={agreements.personalData.url}
                            className="underline"
                            target="_blank"
                            rel="noopener noreferrer"
                          />
                        ),
                      }}
                    />
                  </Label>
                  <FormMessage />
                </div>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="agreeCouponRules"
            render={({ field }) => (
              <FormItem
                className="flex items-start gap-2 [&_[data-slot=label]]:leading-5"
                data-error-anchor={field.name}
              >
                <FormControl>
                  <Checkbox
                    id="agree-coupon-rules"
                    className="mt-[2px]"
                    checked={!!field.value}
                    onCheckedChange={(v) => field.onChange(Boolean(v))}
                  />
                </FormControl>
                <div className="flex-1">
                  <Label htmlFor="agree-coupon-rules" layout="text">
                    <Trans
                      i18nKey="coupons.fieldAgreement3Label"
                      components={{
                        a: (
                          <a
                            href={agreements.couponRules.url}
                            className="underline"
                            target="_blank"
                            rel="noopener noreferrer"
                          />
                        ),
                      }}
                    />
                  </Label>
                  <FormMessage />
                </div>
              </FormItem>
            )}
          />
        </section>
      </form>
    </Form>
  );
});

export default BuyerInfoForm;
